import styles from './../../MaskOverlay.module.scss';

import { noop } from 'lodash';
import React, { memo, useEffect, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';

import {
  useDataMutation,
  useDataProvider,
  useFormPlus,
  useUser,
} from '@work4all/data';
import { useCustomFieldsConfig } from '@work4all/data/lib/custom-fields';
import { useEntityJsonSchema } from '@work4all/data/lib/json-schema/EntityJsonSchemasContext';

import { Contact } from '@work4all/models/lib/Classes/Contact.entity';
import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { Department } from '@work4all/models/lib/Classes/Department.entity';
import { InputAnsprechpartnerRelation } from '@work4all/models/lib/Classes/InputAnsprechpartnerRelation.entity';
import { Salutation } from '@work4all/models/lib/Classes/Salutation.entity';
import { Supplier } from '@work4all/models/lib/Classes/Supplier.entity';
import { DataRequest, SortDirection } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';

import { useJSONSchemaResolver } from '@work4all/utils';
import { useDeepMemo } from '@work4all/utils/lib/hooks/use-deep-memo';
import {
  canDeleteContact,
  canEditContact,
} from '@work4all/utils/lib/permissions';

import { MaskTabContext, MaskTabPanel } from '../../../mask-tabs';
import { INDIVIDUAL_TAB_ID } from '../../components/custom-fields/contants';
import { CUSTOM_FIELDS_DATA } from '../../components/custom-fields/custom-fields-data';
import { IndividualTabPanel } from '../../components/custom-fields/IndividualTabPanel';
import { normalizeCustomFields } from '../../components/custom-fields/normalize-custom-fields';
import { prepareInputWithCustomFields } from '../../components/custom-fields/prepare-input-with-custom-fields';
import { Form } from '../../components/form';
import { MaskContent } from '../../components/MaskContent/MaskContent';
import { HistoryTabPanel } from '../../components/tab-panels/history/HistoryTabPanel';
import {
  MaskContextProvider,
  useMaskConfig,
  useMaskContextValue,
} from '../../hooks/mask-context';
import { useConfirmBeforeCloseMask } from '../../hooks/use-confrm-before-close-mask';
import { EntityRightsContext } from '../../hooks/use-entity-rights';
import { normalizeFormValue } from '../../hooks/useExtendedFormContext';
import { useInitialFormValue } from '../../hooks/useInitialFormValue';
import { MaskControllerProps } from '../../types';
import { pickUpdateFields } from '../../utils/pick-update-fields';
import { useFormUpdate } from '../../utils/use-form-update';
import { useCategoryAssignmentCustomRules } from '../businessPartners/hooks/use-category-assignment-rules/use-category-assignment-rules';
import { useDefaultCategories } from '../businessPartners/hooks/use-default-categories/use-default-categories';
import { useMandatoryCategoryClasses } from '../businessPartners/hooks/use-mandatory-category-classes/use-mandatory-category-classes';

import { ContactsControllerHeader } from './components/ContactsControllerHeader';
import { GeneralTabPanel } from './components/tab-panels/general/GeneralTabPanel';
import { PRTabPanel } from './components/tab-panels/pr/PRTabPanel';
import { ContactMaskFormValue } from './types';

const calculateLetterSalutation = (context: Contact) => {
  const nameExtension = context.nameExtension;
  const salutation = context.salutation;
  const titleExtension = context.titleExtension;
  const name = context.name;
  const firstName = context.firstName;
  const greetingName =
    salutation?.nameOrFirstName === undefined
      ? name
      : salutation.nameOrFirstName
      ? name
      : firstName;

  const greeting = [
    salutation?.standardletterSalutation,
    titleExtension,
    greetingName,
    nameExtension,
  ]
    .join(' ')
    .trim()
    .replace(/\s\s+/g, ' ');
  return { letterSalutation: `${greeting},` };
};

const DEFAULT_ID = -1;

export type ContactSources = Customer | Supplier;
export enum ContactGroups {
  general = 'general',
  relations = 'relations',
  individual = 'individual',
}

export const ContactsOverlayController = (props: MaskControllerProps) => {
  const mask = useMaskConfig(props);
  if (
    !mask.template ||
    !mask.template.entity ||
    !mask.template.ids ||
    !([Entities.customer, Entities.supplier] as string[]).includes(
      mask.template.entity
    )
  ) {
    throw new Error(
      'The contacts overlay needs a reference to a businesspartner or will not work properly'
    );
  }

  const type =
    mask.template.entity === Entities.customer
      ? SdObjType.KUNDE
      : SdObjType.LIEFERANT;

  const schema = useEntityJsonSchema(mask.entity);

  const customFieldEntityType =
    type === SdObjType.KUNDE ? 'contact:customer' : 'contact:supplier';
  const customFields = useCustomFieldsConfig({ entity: customFieldEntityType });

  const requestData = useMemo(() => {
    // we will always filter by id and will always try to normalize the data
    // so an id field will be available
    //contactIds are not unique so this mask needs to be provided with a bpType to work properly
    const filter = [
      { id: { $eq: mask.id } },
      {
        businessPartnerType: {
          $eq: type,
        },
      },
    ];

    const data: Contact = {
      id: null,
      firstName: null,
      name: null,
      displayName: null,
      businessPartnerType: null,
      businessPartnerId: null,
      salutation: {
        id: null,
        name: null,
        standardletterSalutation: null,
      },
      departmentId: null,
      department: {
        id: null,
        name: null,
      },
      businessPartner: {
        id: null,
        businessPartnerType: null,
      },
      letterSalutation: null,
      birthdayDate: null,
      eMail: null,
      eMailPrivate: null,
      faxNumber: null,
      faxPrivate: null,
      phoneNumber: null,
      phoneNumber2: null,
      phoneNumber3: null,
      phoneNumberPrivate: null,
      mobileNumber: null,
      mobileNumberPrivate: null,
      layedOff: null,
      serialLetterLock: null,
      role: null,
      nameExtension: null,
      titleExtension: null,
      addressExtension: null,
      showDepartmentInAddress: null,
      showFunctionInAddress: null,
      note: null,
      city: null,
      postalCode: null,
      country: null,
      street: null,
      isMainContact: null,
      categoryAssignmentList: [
        {
          id: null,
          categoryId: null,
          categoryKindName: null,
          categoryName: null,
        },
      ],
      attachements: [
        {
          id: null,
          index: null,
          objMemberCode: null,
          type: null,
          fileInfos: {
            fileEntityFilename: null,
            downloadUrl: null,
            previewUrl: null,
            fileRequestType: null,
            downloadMimeType: null,
            previewMimeType: null,
            fileSize: null,
          },
        },
      ],
      customFieldList: [CUSTOM_FIELDS_DATA],
    };

    return {
      filter,
      entity: Entities.contact,
      data,
    };
  }, [mask.id, type]);

  const initialFormValue = useInitialFormValue<Contact>(
    requestData,
    mask.isCreateMode
  );

  const { data: mandatoryCategoryClasses } = useMandatoryCategoryClasses(
    mask.template.entity as Entities,
    true
  );

  const customRules = useCategoryAssignmentCustomRules({
    mandatoryCategoryClasses,
    skip: !mask.isCreateMode,
  });

  const salutationRequest = useMemo<DataRequest>(() => {
    const salutationData: Salutation = {
      id: null,
      name: null,
      standardletterSalutation: null,
      isFemale: null,
      isMale: null,
      title: null,
      number: null,
      nameOrFirstName: null,
    };
    return {
      entity: Entities.salutation,
      data: salutationData,
      completeDataResponse: true,
      filter: [{ isMale: { $eq: true } }],
      sort: [{ field: 'number', direction: SortDirection.ASCENDING }],
    };
  }, []);

  const salutationResponse = useDataProvider<Salutation>(salutationRequest);

  const { data: defaultCategories } = useDefaultCategories(
    mask.template.entity as Entities,
    true
  );

  const newEntityData: Contact = useMemo(() => {
    return {
      businessPartnerType: type,
      businessPartnerId: parseInt(`${mask.template.ids[0]}`, 10),
      businessPartner: {
        id: parseInt(`${mask.template.ids[0]}`, 10),
        businessPartnerType: type,
      },
      salutation: salutationResponse?.data?.[0],
      salutationId: salutationResponse?.data?.[0]?.id,
      categoryAssignmentList: defaultCategories.map((c) => ({
        id: DEFAULT_ID, //Mark category as default, that should be added on save
        categoryId: c.id,
        categoryName: c.name,
        categoryKindId: c.categoryClass.id,
        categoryKindName: c.categoryClass.name,
      })),
    };
  }, [defaultCategories, mask.template.ids, salutationResponse?.data, type]);

  const dataRaw = mask.isCreateMode
    ? newEntityData
    : initialFormValue.value ?? newEntityData;

  const data = useDeepMemo(() => {
    return normalizeCustomFields(normalizeFormValue(dataRaw), customFields);
  }, [dataRaw, customFields]);

  const resolver = useJSONSchemaResolver(schema, customRules);
  const form = useFormPlus<ContactMaskFormValue>({
    resolver,
    mode: 'onChange',
    defaultValues: data,
    shouldFocusError: false,
    context: {
      schema,
    },
  });

  const { handleSubmit, reset, formState, setValue } = form;
  const { dirtyFields } = formState;

  const [mutate] = useDataMutation<
    Contact,
    EMode.upsert,
    InputAnsprechpartnerRelation
  >({
    entity: Entities.contact,
    mutationType: EMode.upsert,
    responseData: requestData.data as unknown as Contact<EMode.entity>,
    onCompleted: props.onAfterSave,
    resetStore: !mask.isCreateMode,
  });

  const isDirty = Object.keys(dirtyFields).length > 0;

  useConfirmBeforeCloseMask(isDirty);

  useEffect(() => {
    reset(data);
  }, [reset, data]);

  useFormUpdate(
    {
      salutation: (salutation) => {
        return {
          salutationId: salutation?.id,
          ...calculateLetterSalutation(salutation),
        };
      },
      department: (department: Department) => {
        return { departmentId: department?.id || 0 };
      },
      nameExtension: (nameExtension, _prevNameExtension, context) => {
        return calculateLetterSalutation({ ...context, nameExtension });
      },
      titleExtension: (titleExtension, _prevTitleExtension, context) => {
        return calculateLetterSalutation({ ...context, titleExtension });
      },
      name: (name, _prevName, context) => {
        return calculateLetterSalutation({ ...context, name });
      },
      firstName: (firstName, _prevFirstName, context) => {
        return calculateLetterSalutation({ ...context, firstName });
      },
    },
    form
  );

  useEffect(() => {
    const greeting = calculateLetterSalutation(data);
    setValue('letterSalutation', greeting.letterSalutation, {
      shouldDirty: true,
    });
  }, [data, setValue]);

  const user = useUser();

  const submitForm = async (submitData: ContactMaskFormValue) => {
    const relations: InputAnsprechpartnerRelation = {
      categoryMarks: {
        add: submitData?.categoryAssignmentList
          ?.filter(
            (currentAssignment) =>
              !data?.categoryAssignmentList?.find(
                (prevAssignment) =>
                  currentAssignment.categoryId === prevAssignment.categoryId &&
                  prevAssignment.id !== DEFAULT_ID
              )
          )
          ?.map((assignment) => ({
            categoryCode: assignment.categoryId,
          })),
        remove: data?.categoryAssignmentList
          ?.filter(
            (prevAssignment) =>
              !submitData?.categoryAssignmentList.find(
                (currentAssignment) =>
                  currentAssignment.categoryId === prevAssignment.categoryId
              )
          )
          ?.map((assignment) => assignment.categoryId),
      },
    };

    const isBirthdayBeingCleared =
      !submitData?.birthdayDate || submitData?.birthdayDate?.length === 0;

    if (isBirthdayBeingCleared) {
      submitData.birthdayDate = null;
      submitData.birthdayDay = null;
      submitData.birthdayMonth = null;
      submitData.birthdayYear = null;
    }

    const updateRaw = mask.isCreateMode
      ? { ...newEntityData, ...submitData }
      : pickUpdateFields(submitData, {
          ...formState.dirtyFields,
          ...(isBirthdayBeingCleared && {
            birthdayDate: true,
            birthdayDay: true,
            birthdayMonth: true,
            birthdayYear: true,
          }),
          businessPartnerType: true,
        });

    const updateMapped = prepareInputWithCustomFields(updateRaw, customFields);

    await mutate(updateMapped, relations ? { relations } : undefined);
  };

  const entityRights = useMemo(
    () => ({
      create: canEditContact(user, { businessPartnerType: type }),
      read: false,
      update: canEditContact(user, { businessPartnerType: type }),
      delete: canDeleteContact(user, { businessPartnerType: type }),
    }),
    [type, user]
  );

  const maskContext = useMaskContextValue({ ...mask, data, customFields });

  const shouldRenderIndividualTab = customFields && customFields.length > 0;

  return (
    <MaskContextProvider value={maskContext}>
      <EntityRightsContext.Provider value={entityRights}>
        <FormProvider {...form}>
          <MaskTabContext defaultValue="general">
            <Form
              className={styles.maskForm}
              onSubmit={handleSubmit(submitForm)}
            >
              <ContactsControllerHeader
                onDelete={props.onDelete}
                shouldRenderIndividualTab={shouldRenderIndividualTab}
                entityRights={entityRights}
              />
              <ContactTabPanels
                renderIndividualTab={shouldRenderIndividualTab}
                isCreateMode={mask.isCreateMode}
                businessPartnerType={type}
              />
            </Form>
          </MaskTabContext>
        </FormProvider>
      </EntityRightsContext.Provider>
    </MaskContextProvider>
  );
};

const ContactTabPanels = memo(function ContactTabPanels({
  renderIndividualTab,
  isCreateMode,
  businessPartnerType,
}: {
  renderIndividualTab: boolean;
  isCreateMode: boolean;
  businessPartnerType: SdObjType;
}) {
  return (
    <MaskContent>
      <MaskTabPanel value="general">
        <GeneralTabPanel />
      </MaskTabPanel>
      <MaskTabPanel value="private">
        <PRTabPanel />
      </MaskTabPanel>
      {renderIndividualTab && (
        <MaskTabPanel value={INDIVIDUAL_TAB_ID}>
          <IndividualTabPanel />
        </MaskTabPanel>
      )}
      {!isCreateMode && (
        <MaskTabPanel value="history">
          <HistoryTabPanel
            overrides={{
              entity:
                businessPartnerType === SdObjType.KUNDE
                  ? Entities.customerContact
                  : Entities.supplierContact,
            }}
          />
        </MaskTabPanel>
      )}
    </MaskContent>
  );
});

export const EMailSuggestionContext = React.createContext({
  enableEMailSuggestions: true,
  setEnableEMailSuggestions: noop,
});
